import { useEffect, useRef, useState } from "react";
import { ChatTeardropDots, PaperPlaneRight, X, Sparkle } from "@phosphor-icons/react";
import { api, extractApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLocation } from "react-router-dom";

/** Floating AI Coach chat. Anchored bottom-right on every authenticated page.
 *  Talks to /api/ai/chat (universal LLM key). Sends the active plan id when
 *  the user is currently viewing/editing a specific plan so the coach has
 *  full context. */
export default function AICoachChat() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "I'm your AI Coach. Ask anything about your plan, programming tweaks, " +
        "swaps for injuries, or general FM 7-22 questions. I have your assessment " +
        "and current plan in context.",
    },
  ]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages.length, open, busy]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Pluck plan_id from /plans/:id or /builder/:id or /session/:id/.. routes.
  const planIdFromUrl = (() => {
    const m = location.pathname.match(/\/(?:plans|builder|session)\/([0-9a-f]{24})/i);
    return m ? m[1] : null;
  })();

  if (!user) return null;  // anonymous users shouldn't see the chat
  // Hide entirely during a live workout session — the floating launcher
  // would otherwise sit on top of the "Finish Session" button. Operators
  // should be focused, not chatting, mid-set.
  if (/^\/session\//.test(location.pathname)) return null;

  const send = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setDraft("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", {
        plan_id: planIdFromUrl,
        messages: next.filter((m) => m.role !== "system"),  // never send system noise
        session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "⚠️ " + extractApiError(e) }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Launcher — positioned ABOVE the "Made with Emergent" watermark
          (which sits at bottom-right). We bump the button up by 80px and
          use z-[60] to ensure pointer events route to us. */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          style={{ bottom: "5.5rem", right: "1rem" }}
          className="fixed z-[60] bg-volt text-black p-4 rounded-full shadow-2xl ring-2 ring-volt/40 hover:scale-105 transition-transform group"
          aria-label="Open AI Coach"
          data-testid="ai-coach-launcher"
        >
          <Sparkle size={22} weight="fill" />
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-card border border-border px-3 py-1 text-xs uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            Talk to AI Coach
          </span>
        </button>
      )}

      {/* Panel — bump above watermark by leaving 5rem at bottom on desktop. */}
      {open && (
        <div
          style={{ bottom: "5rem" }}
          className="fixed right-0 sm:right-4 z-[60] m-0 sm:m-0 w-full sm:w-[420px] h-[80vh] sm:h-[600px] bg-card border border-border flex flex-col shadow-2xl"
          data-testid="ai-coach-panel"
        >
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-background">
            <ChatTeardropDots size={18} weight="fill" className="text-volt" />
            <div className="label-mono text-volt">// AI COACH</div>
            <div className="text-[10px] text-muted-foreground ml-auto">
              {planIdFromUrl ? "Plan context loaded" : "General"}
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-muted-foreground hover:text-volt"
              aria-label="Close"
              data-testid="ai-coach-close"
            >
              <X size={16} weight="bold" />
            </button>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-coach-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[88%] px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                    m.role === "user"
                      ? "bg-volt text-black"
                      : "bg-background border border-border text-foreground"
                  }`}
                  data-testid={`ai-msg-${i}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start" data-testid="ai-busy">
                <div className="bg-background border border-border px-3 py-2 text-xs text-muted-foreground">
                  Coach is thinking<span className="inline-block animate-pulse">…</span>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-border p-3 flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={2}
              placeholder="Ask anything…"
              className="flex-1 bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-volt resize-none"
              data-testid="ai-input"
            />
            <button
              onClick={send}
              disabled={!draft.trim() || busy}
              className="bg-volt text-black p-3 disabled:opacity-40 hover:opacity-90"
              aria-label="Send"
              data-testid="ai-send"
            >
              <PaperPlaneRight size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
